/**
 * Section « Personnalisation ».
 *
 * Les taux sont rapportés aux utilisateurs ACTIFS de la période, pas au parc de
 * comptes : la plupart des comptes n'étaient pas actifs, et les inclure au
 * dénominateur donnerait un taux faussement bas.
 */

import { BrightnessChart } from '../components/BrightnessChart';
import { LanguageChart } from '../components/LanguageChart';
import { SectionShell } from '../components/SectionShell';
import { StatTile } from '../components/StatTile';
import { useAuth } from '../hooks/useAuth';
import { usePersonalizationStats } from '../hooks/usePeriodStats';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { formatInteger, formatPercent } from '../lib/format';
import { languageLabel } from '@shared/types/stats';

export function PersonalizationPage() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();

  const { data, error, isPending, isFetching } = usePersonalizationStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
  });

  const summary = data?.summary;

  return (
    <SectionShell
      lastSyncAt={data?.meta.lastSyncAt}
      isPending={isPending}
      isFetching={isFetching}
      error={error}
      hasData={Boolean(data)}
    >
      <div className="kpi-row">
        <StatTile
          label="Règlent la luminosité"
          value={formatPercent(summary?.brightnessUserShare ?? 0)}
          hint={`${formatInteger(summary?.brightnessUsers ?? 0)} des ${formatInteger(summary?.activeUsers ?? 0)} actifs`}
        />
        <StatTile
          label="Changements de luminosité"
          value={formatInteger(summary?.brightnessChanges ?? 0)}
          hint={
            summary?.brightnessMode != null
              ? `valeur la plus choisie : ${summary.brightnessMode}`
              : 'aucun changement'
          }
        />
        <StatTile
          label="Luminosité médiane"
          value={summary?.brightnessMedian != null ? String(summary.brightnessMedian) : '—'}
          hint="sur une échelle de 1 à 100"
        />
        <StatTile
          label="Changent la langue"
          value={formatPercent(summary?.languageUserShare ?? 0)}
          hint={`${formatInteger(summary?.languageUsers ?? 0)} des ${formatInteger(summary?.activeUsers ?? 0)} actifs`}
        />
        <StatTile
          label="Langue la plus choisie"
          value={summary?.topLanguage ? languageLabel(summary.topLanguage) : '—'}
          hint={`${formatInteger(summary?.languageChanges ?? 0)} changements`}
        />
      </div>

      <div className="chart-grid">
        <BrightnessChart
          data={data?.brightnessDistribution ?? []}
          median={summary?.brightnessMedian ?? null}
        />
        <LanguageChart data={data?.languages ?? []} />
      </div>
    </SectionShell>
  );
}
